import { create } from "zustand";
import { Colaborador, ColaboradorComProgress, GuRanking, CertificacaoProgress, CertificationData } from "../types";
import { calcIndividualPoints, getStatus, buildGuRankings } from "../lib/scoring";
import { loadProgress, saveProgress } from "../lib/mock-generator";
import { mapTrilhas } from "../lib/trilhas";
import { stripNicknameParens } from "../lib/name-utils";
import type { LmsUserName } from "../services/universityApi";

interface DataState {
  colaboradores: Colaborador[];
  progressMap: Map<string, CertificacaoProgress>;
  membersWithProgress: ColaboradorComProgress[];
  guRankings: GuRanking[];
  isLoading: boolean;
  apiError: string | null;
  usingApiData: boolean;
  loadData: (colaboradores: Colaborador[]) => void;
  setApiError: (msg: string | null) => void;
  loadDataWithCertifications: (colaboradores: Colaborador[], certMap: Map<string, CertificationData>) => void;
  enrichNamesFromLms: (directory: Map<string, LmsUserName>) => void;
  updateProgress: (progress: CertificacaoProgress) => void;
  resetProgress: () => void;
}

function buildMembers(
  colaboradores: Colaborador[],
  progressMap: Map<string, CertificacaoProgress>,
): ColaboradorComProgress[] {
  return colaboradores.map((c) => {
    const progress = progressMap.get(c.login) ?? {
      login: c.login,
      trilhaProgress: mapTrilhas(() => 0),
      certifiedAt: null,
      prizeCertifiedAt: null,
      trilhaCertifiedAt: mapTrilhas(() => null),
    };
    const totalPoints = calcIndividualPoints(progress);
    const status = getStatus(progress);
    return { ...c, progress, totalPoints, status };
  });
}

export const useDataStore = create<DataState>((set, get) => ({
  colaboradores: [],
  progressMap: new Map(),
  membersWithProgress: [],
  guRankings: [],
  isLoading: true,
  apiError: null,
  usingApiData: false,

  // Fallback: load from localStorage mock data (used when API is unavailable)
  loadData: (colaboradores) => {
    const logins = colaboradores.map((c) => c.login);
    const progressMap = loadProgress(logins);
    const membersWithProgress = buildMembers(colaboradores, progressMap);
    const guRankings = buildGuRankings(membersWithProgress);
    set({ colaboradores, progressMap, membersWithProgress, guRankings, isLoading: false, usingApiData: false });
  },

  // Surfaces a fatal load error (e.g. /api/people unreachable) so the UI can
  // show a non-silent error state instead of an empty dashboard.
  setApiError: (msg) => {
    set({ apiError: msg, isLoading: false });
  },

  // Primary: load from real API certification data
  loadDataWithCertifications: (colaboradores, certMap) => {
    const progressMap = new Map<string, CertificacaoProgress>();

    for (const c of colaboradores) {
      const loginKey = c.login.toLowerCase();
      const cert = certMap.get(loginKey) ?? certMap.get(c.login);

      const trilhaProgress = mapTrilhas((trilha) =>
        Math.round((cert?.trilhaProgress[trilha.id] ?? 0) * 100),
      );
      const trilhaCertifiedAt = mapTrilhas(
        (trilha) => cert?.trilhaCertifiedAt[trilha.id] ?? null,
      );
      progressMap.set(c.login, {
        login: c.login,
        trilhaProgress,
        certifiedAt: cert?.certifiedAt ?? null,
        prizeCertifiedAt: cert?.prizeCertifiedAt ?? null,
        trilhaCertifiedAt,
      });
    }

    const membersWithProgress = buildMembers(colaboradores, progressMap);
    const guRankings = buildGuRankings(membersWithProgress);
    set({
      colaboradores,
      progressMap,
      membersWithProgress,
      guRankings,
      isLoading: false,
      apiError: null,
      usingApiData: true,
    });
  },

  // Sobrescreve `name` com `firstName lastName` do TalentLMS quando há match.
  // Logins sem entrada no diretório preservam o name original (fallback do CSV).
  enrichNamesFromLms: (directory) => {
    const { colaboradores, progressMap } = get();
    let changed = false;
    const next = colaboradores.map((c) => {
      const entry = directory.get(c.login.toLowerCase());
      if (!entry) return c;
      const fullName = stripNicknameParens(
        [entry.firstName, entry.lastName].filter(Boolean).join(" "),
      );
      if (!fullName || fullName === c.name) return c;
      changed = true;
      return { ...c, name: fullName };
    });
    if (!changed) return;
    const membersWithProgress = buildMembers(next, progressMap);
    const guRankings = buildGuRankings(membersWithProgress);
    set({ colaboradores: next, membersWithProgress, guRankings });
  },

  updateProgress: (progress) => {
    const { progressMap, colaboradores } = get();
    const newMap = new Map(progressMap);
    newMap.set(progress.login, progress);
    saveProgress(newMap);
    const membersWithProgress = buildMembers(colaboradores, newMap);
    const guRankings = buildGuRankings(membersWithProgress);
    set({ progressMap: newMap, membersWithProgress, guRankings });
  },

  resetProgress: () => {
    const { colaboradores } = get();
    localStorage.removeItem("agentic_league_progress");
    const logins = colaboradores.map((c) => c.login);
    const progressMap = loadProgress(logins);
    const membersWithProgress = buildMembers(colaboradores, progressMap);
    const guRankings = buildGuRankings(membersWithProgress);
    set({ progressMap, membersWithProgress, guRankings, usingApiData: false });
  },
}));
